<template>
  <div class="sign-up">
    <SignBanner result="register" />
    <ModalComponentBody>
      <SignTitle>
        {{ messages?.register }}
      </SignTitle>
      <SignText>
        {{ messages?.createAnAccount }}
      </SignText>
      <form class="sign-up-form" @submit.prevent="handlerSubmit">
        <DefInput
          required
          :placeholder="messages?.userName"
          :name="isUkrainePhone ? 'USER_NAME' : 'name'"
          :is-show-error="Boolean(errors?.registerUkraine)"
          :error-text="errors?.registerUkraine"
        />
        <DefEmail v-if="!isUkrainePhone" required placeholder="Email" name="email" />
        <DefPhone
          required
          :placeholder="messages?.phoneNumber"
          :name="isUkrainePhone ? 'USER_LOGIN' : 'phone'"
          :initial-country="initialCountry"
          :is-show-error="Boolean(errors?.signUpByPhone)"
          :error-text="errors?.signUpByPhone"
          @countrychange="countrychange"
        />
        <DefInputPassword
          v-if="!isUkrainePhone"
          required
          :placeholder="messages?.password"
          :is-show-error="Boolean(errors && errors.registerSendLink)"
          name="password"
        >
          {{ errors?.registerSendLink }}
        </DefInputPassword>
        <DefInputPassword
          v-if="!isUkrainePhone"
          required
          :placeholder="messages?.confirmPassword"
          name="confirmPassword"
        />
        <DefButtonPrimary type="submit">{{ messages?.registerBtn }}</DefButtonPrimary>
        <DefButtonSecondary type="button" @click="handlerMoveToSignIn">{{
          messages?.iHaveAnAccount
        }}</DefButtonSecondary>
      </form>
    </ModalComponentBody>
  </div>
</template>
<script setup lang="ts">
import { computed, reactive } from 'vue';
import SignBanner from '@components/Cabinet/Entry/SignBanner.vue';
import ModalComponentBody from '@use/useModal/ModalComponentBody.vue';
import SignTitle from '@components/Cabinet/Entry/SignTitle.vue';
import SignText from '@components/Cabinet/Entry/SignText.vue';
import DefInput from '@components/Def/DefInput/DefInput.vue';
import DefEmail from '@components/Def/DefEmail/DefEmail.vue';
import DefPhone from '@components/Def/DefPhone/DefPhone.vue';
import DefInputPassword from '@components/Def/DefInput/DefInputPassword.vue';
import DefButtonPrimary from '@components/Def/DefButtonPrimary/DefButtonPrimary.vue';
import DefButtonSecondary from '@components/Def/DefButtonSecondary/DefButtonSecondary.vue';
import Recaptcha3 from '@/utils/Recaptcha3';
import SignIn from '@components/Cabinet/Entry/SignIn/SignIn.vue';
import useModal from '@use/useModal/useModal';
import SignUpSuccess from '@components/Cabinet/Entry/SignUp/SignUpSuccess.vue';
import SignUpConfirmPhone from '@components/Cabinet/Entry/SignUp/SignUpConfirmPhone.vue';
import { useResultsStore } from '@/storePinia/stores/results';
import { usePersonalStore } from '@/storePinia/stores/personal';

const modal = useModal();
const resultsStore = useResultsStore();
const personalStore = usePersonalStore();

const messages = computed(() => resultsStore.messages?.entry);
const errors = computed(() => personalStore.errors);

const handlerMoveToSignIn = () => {
  modal.show(
    SignIn,
    {},
    {
      width: '900px',
      backgroundClose: false,
    }
  );
};
const componentState = reactive({
  selectedCountryCode: '',
});

const countrychange = (countryCode: string) => {
  componentState.selectedCountryCode = countryCode;
};

const countryISOCode = computed(() => resultsStore.getSelectedCountry?.UF_COUNTRY_ISO_CODE);
const initialCountry = computed(() => {
  if (countryISOCode.value) {
    return countryISOCode.value;
  }
  return undefined;
});

const isUkrainePhone = computed(() => {
  if (componentState.selectedCountryCode === '' && initialCountry.value?.toLowerCase() === 'ua') {
    return true;
  }
  return componentState.selectedCountryCode === 'ua';
});

const handlerSubmit = async (e: SubmitEvent): void => {
  const formData: FormData = new FormData(e.target as HTMLFormElement);

  const token = await Recaptcha3.getSubmitToken();
  formData.append('g-recaptcha-response', token);

  if (isUkrainePhone.value) {
    const response = await personalStore.signUpByPhone(formData);

    if (response) {
      modal.show(
        SignUpConfirmPhone,
        {
          result: {
            formData,
          },
        },
        {
          backgroundClose: false,
          width: '900px',
        }
      );
    }
  } else {
    const response = await personalStore.sendRegisterLinkToGlobalUser(formData);

    if (response?.status === true) {
      modal.show(
        SignUpSuccess,
        {
          text: response.text,
        },
        {
          width: '900px',
          backgroundClose: false,
        }
      );
    }
  }
};
</script>
<style lang="scss" scoped>
.sign-up {
  @include point-md {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  :deep(.def-button-primary) {
    margin-top: 4px;
  }
}
.sign-up-form {
  display: grid;
  grid-row-gap: 12px;
}
</style>
